import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scan, ZoomIn, ZoomOut } from 'lucide-react';
import { useZoomPan } from '@/components/climbs/board-grid/useZoomPan';
import {
  alongLine,
  boardCells,
  cellKey,
  disabledCellSet,
  extraHoldSpot,
  FULL_IMAGE_CROP,
  holdPosition,
  isExtraHold,
  isShapedWall,
  MIN_CROP_SIZE,
  normalizeCrop,
  quadCoordinates,
  quadPosition,
  taperFor,
  wallHoldWidth,
} from '@/components/climbs/board-grid/geometry';
import type {
  BoardCalibration,
  BoardDisabledCells,
  BoardExtraHold,
  BoardExtraHoldMap,
  BoardHoldMap,
  BoardImageCrop,
  BoardShapes,
  CalibrationPoint,
} from '../../types/device';

type CornerKey = 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right';

const CORNERS: CornerKey[] = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];

/**
 * What a press on the picture is for. The jobs done here all want the same
 * pointer — drawing a line, dragging a corner, dragging the crop, dropping a
 * hold, crossing a cell off — so only one of them is live at a time.
 */
export type CalibrationTool = 'shapes' | 'corners' | 'crop' | 'extras' | 'cells';

/**
 * How near two ends of two lines have to be to count as the same point, in the
 * quad's own units. Lines are drawn one after another, each starting where the
 * last one stopped, so the joint is exact — this is for the one dragged apart
 * and put back by hand, and for floating point having been through a
 * perspective and back.
 */
const JOINT_TOLERANCE = 1e-6;

/** Whether two ends of lines are the same joint, and so move together. */
function sameJoint(a: BoardExtraHold, b: BoardExtraHold): boolean {
  return Math.abs(a.u - b.u) < JOINT_TOLERANCE && Math.abs(a.v - b.v) < JOINT_TOLERANCE;
}

/** The corners of the crop window, named by the two edges each one moves. */
const CROP_HANDLES = ['nw', 'ne', 'sw', 'se'] as const;

type CropHandle = (typeof CROP_HANDLES)[number];

/**
 * A gesture crossing cells off, and what it has decided so far.
 *
 * `stroke` is the pointer run over the holds that are not there — the way a bare
 * patch is actually looked at, hold by hold. It carries where it was last seen
 * (so the run between two moves is what counts, not the two dots) and which way
 * it is painting, settled by the first cell it meets: over a hold that is there,
 * it crosses off; over one already crossed off, it puts back. That is what makes
 * a stroke undoable by running back over it.
 *
 * `box` is shift-drag: the same thing said about a whole panel at once.
 */
type CellGesture =
  | { kind: 'stroke'; at: CalibrationPoint; crossOff: boolean | null }
  | { kind: 'box'; from: CalibrationPoint; to: CalibrationPoint };

const DEFAULT_CALIBRATION: BoardCalibration = {
  top_left: { x: 0.1, y: 0.1 },
  top_right: { x: 0.9, y: 0.1 },
  bottom_left: { x: 0.1, y: 0.9 },
  bottom_right: { x: 0.9, y: 0.9 },
};

/** Same language as the zoom controls floating on a board photo elsewhere. */
const CONTROL =
  'flex items-center justify-center rounded-full bg-black/50 p-1.5 text-white transition-colors hover:bg-black/70 disabled:opacity-40 disabled:hover:bg-black/50';

interface BoardCalibrationEditorProps {
  /**
   * The photo as it was uploaded — the whole of it, crop or no crop. Everything
   * here is measured against this picture: it is the only frame a crop can be
   * widened from, and the only one where a corner the crop cut off is still
   * somewhere you can reach.
   */
  imageUrl: string;
  value: BoardCalibration | null;
  onChange: (calibration: BoardCalibration) => void;
  /**
   * The wall's grid. Given both, every cell is drawn where the corners put it,
   * which is the only way to tell whether they are right: four handles on a
   * photo say nothing about whether the hold in the middle of the wall lands on
   * a hold. Left out, only the quad is drawn.
   */
  rows?: number | null;
  columns?: number | null;
  /** A mapped layout's own holds, so the preview is the wall and not a lattice. */
  holds?: BoardHoldMap | null;
  /**
   * The holds that are not on the grid. Given an `onExtraHoldsChange`, they can
   * be placed and taken off here; without one they are only drawn, which is
   * what a wall with a device attached gets — there is no LED between two
   * t-nuts, so it can never point at one of these.
   */
  extraHolds?: BoardExtraHoldMap | null;
  onExtraHoldsChange?: (extraHolds: BoardExtraHoldMap) => void;
  /**
   * The cells the wall has nothing on. Drawn crossed off rather than left out,
   * because this is the one screen where they have to be visible to be put back.
   */
  disabledCells?: BoardDisabledCells | null;
  onDisabledCellsChange?: (cells: BoardDisabledCells) => void;
  /**
   * The lines this wall's holds are strung along, drawn on the photo one after
   * another. Any at all and they are the wall: the grid is not drawn, and the
   * holds come from the lines.
   */
  shapes?: BoardShapes | null;
  onShapesChange?: (shapes: BoardShapes) => void;
  /**
   * The line the list beside the picture has in hand, so the wall can show
   * which one is being talked about — and so drawing on the wall moves the
   * selection with it.
   */
  selectedShape?: number | null;
  onSelectShape?: (index: number | null) => void;
  /** The window of the photo the board is drawn through; all of it by default. */
  crop?: BoardImageCrop;
  onCropChange?: (crop: BoardImageCrop) => void;
  tool?: CalibrationTool;
}

/**
 * Marks the four grid corners on a board image, once per board. The corners are
 * stored as normalized (0-1) points so they stay aligned at any rendered size;
 * the grid overlay on the climb-create screen interpolates within them.
 */
export default function BoardCalibrationEditor({
  imageUrl,
  value,
  onChange,
  rows = null,
  columns = null,
  holds = null,
  extraHolds = null,
  onExtraHoldsChange,
  disabledCells = null,
  onDisabledCellsChange,
  shapes = null,
  onShapesChange,
  selectedShape = null,
  onSelectShape,
  crop = FULL_IMAGE_CROP,
  onCropChange,
  tool = 'corners',
}: BoardCalibrationEditorProps) {
  const { t } = useTranslation();
  // On the layer that moves, not the frame that clips it: a transformed box
  // reports its *rendered* rectangle, so reading a pointer against it goes on
  // giving the same point of the picture at any zoom, with no unwinding of the
  // transform anywhere in here.
  const containerRef = useRef<HTMLDivElement>(null);
  // A wall is 25 holds across and a corner has to land on one of them; on a
  // photo drawn at panel width that is a couple of pixels. Same gestures as the
  // board a setter paints on — ⌘ and scroll to zoom, scroll to move about.
  const zoom = useZoomPan();
  const [dragging, setDragging] = useState<CornerKey | null>(null);
  // The end of a line being dragged, while it is being dragged.
  const [shapeDrag, setShapeDrag] = useState<{ index: number; end: 'from' | 'to' } | null>(null);
  /**
   * The hold being dragged along its own line, while it is being dragged.
   *
   * It does not move: what moves is how the line's holds are bunched along it,
   * which is one number settled by this one hold's place (see taperFor). So the
   * hold follows the pointer because the whole run follows it.
   */
  const [holdDrag, setHoldDrag] = useState<{ index: number; col: number } | null>(null);
  /**
   * Where the first line will start, before there is a line to start it.
   *
   * Drawing a wall is a run of clicks, each one the end of a line and the start
   * of the next. The very first click has no line before it to be the end of, so
   * it is held here until the second click makes a line out of the pair.
   */
  const [pendingStart, setPendingStart] = useState<{ u: number; v: number } | null>(null);
  // The gesture crossing cells off, while it is being made. Two of them, because
  // there are two ways a gym describes a patch of bare panel: run the pointer
  // over the holds that are not there, or throw a box around the lot.
  const [cellDrag, setCellDrag] = useState<CellGesture | null>(null);
  /**
   * The crossed-off cells as they stand *within* a stroke, not merely between
   * renders.
   *
   * A pointer travelling across a wall hands us a move every few pixels, and
   * React is under no obligation to have re-rendered with the answer to one
   * before delivering the next. Reading the prop then would compute the second
   * move's answer from the list as it was before the first, and the cells the
   * first move painted would fall back out — a stroke with holes in it, which is
   * exactly what this gesture exists to avoid.
   */
  const paintedRef = useRef<BoardDisabledCells>(disabledCells ?? []);
  useEffect(() => {
    paintedRef.current = disabledCells ?? [];
  }, [disabledCells]);
  // A crop being dragged, and what it looked like when the drag started: moving
  // the window needs the pointer's offset inside it, which is only knowable from
  // where the press landed.
  const [cropDrag, setCropDrag] = useState<{
    handle: CropHandle | 'move';
    from: CalibrationPoint;
    crop: BoardImageCrop;
  } | null>(null);
  // Width over height of the photo, for sizing the preview's cells — see
  // gridHoldWidth. Null until it loads, which only makes them a little large.
  const [aspect, setAspect] = useState<number | null>(null);

  const calibration: Required<BoardCalibration> = useMemo(
    () => ({
      top_left: value?.top_left ?? DEFAULT_CALIBRATION.top_left!,
      top_right: value?.top_right ?? DEFAULT_CALIBRATION.top_right!,
      bottom_left: value?.bottom_left ?? DEFAULT_CALIBRATION.bottom_left!,
      bottom_right: value?.bottom_right ?? DEFAULT_CALIBRATION.bottom_right!,
    }),
    [value]
  );

  const pointFromEvent = useCallback((clientX: number, clientY: number): CalibrationPoint => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  }, []);

  const off = useMemo(() => disabledCellSet(disabledCells), [disabledCells]);

  const drawn = isShapedWall(shapes);

  /**
   * Whether the quad is worth drawing and taking hold of.
   *
   * Always, on a grid: that is the only thing the four corners describe. On a
   * drawn wall only while they are the tool in hand, because that is when the
   * quad is around the lines and means something — see pickTool. The rest of
   * the time it is nailed to the picture, where an outline would be a blue box
   * over the whole photo and the handles would be in its corners, saying
   * nothing about the wall and in the way of drawing it.
   */
  const showQuad = !drawn || tool === 'corners';

  // Where each of the wall's holds lands under the corners as they stand. This
  // is the answer the four handles are being dragged towards, so it is redrawn
  // as they move rather than only once they are let go.
  //
  // The crossed-off cells are in here too, unlike everywhere else that draws a
  // board: this is the screen where they are crossed off, so it is also the one
  // place they have to stay visible to be put back.
  const preview = useMemo(() => {
    if (!drawn && (!rows || !columns)) return null;

    const width = wallHoldWidth(calibration, rows ?? 0, columns ?? 0, aspect, shapes);

    return boardCells(holds, rows ?? 0, columns ?? 0, extraHolds, null, shapes).map(({ row, col }) => {
      const point = holdPosition(calibration, rows ?? 0, columns ?? 0, extraHolds, row, col, shapes);
      const extra = isExtraHold(row) ? col : null;

      return {
        key: `${row}-${col}`,
        row,
        col,
        point,
        width,
        extra,
        disabled: extra === null && off.has(cellKey({ row, col })),
      };
    });
  }, [calibration, rows, columns, holds, extraHolds, shapes, drawn, aspect, off]);

  /**
   * The holds of the line in hand that can be dragged along it.
   *
   * Only the ones between its ends. The two ends are already on the first and
   * last hole — they were clicked there — and they are moved by moving the
   * line; what is left to say is how the holds in between are spread, and any
   * one of them says all of it (see taperFor). A line of two holds has nothing
   * between them and so nothing to drag.
   */
  const taperHandles = useMemo(() => {
    if (tool !== 'shapes' || selectedShape === null || !preview || !onShapesChange) return [];

    const shape = shapes?.[selectedShape];
    const last = Math.max(0, Math.trunc(shape?.holds ?? 0)) - 1;
    if (!shape || last < 2) return [];

    return preview.filter(
      ({ row, col, extra }) => extra === null && row === selectedShape && col > 0 && col < last
    );
  }, [tool, selectedShape, preview, shapes, onShapesChange]);

  /**
   * The ends of the lines, as the points you can take hold of.
   *
   * One handle per joint rather than two: the lines are drawn one after another,
   * each starting where the last one stopped, so a joint is two ends of two
   * lines in the same place — and a pair of handles stacked on one hold is a
   * coin toss over which one a drag picks up. The end of a line somebody has
   * since dragged off its neighbour gets its own handle back.
   */
  const shapeHandles = useMemo(() => {
    if (!isShapedWall(shapes)) return [];

    return shapes.flatMap((shape, index) => {
      const previous = shapes[index - 1];
      const joint = previous && sameJoint(previous.to, shape.from);

      return [
        ...(joint ? [] : [ { index, end: 'from' as const, point: shape.from } ]),
        { index, end: 'to' as const, point: shape.to },
      ];
    });
  }, [shapes]);

  /**
   * Moves one end of one line, carrying the line next to it along when the two
   * share the joint: a wall is drawn as one run, and a corner of it that came
   * apart when it was dragged would leave a gap no hold sits in.
   */
  const moveShapeEnd = useCallback(
    (index: number, end: 'from' | 'to', spot: { u: number; v: number }) => {
      if (!onShapesChange || !shapes) return;

      const moved = shapes[index];
      if (!moved) return;

      const neighbour = end === 'to' ? index + 1 : index - 1;
      const joined =
        end === 'to'
          ? shapes[neighbour] && sameJoint(moved.to, shapes[neighbour].from)
          : shapes[neighbour] && sameJoint(moved.from, shapes[neighbour].to);

      onShapesChange(
        shapes.map((shape, at) => {
          if (at === index) return { ...shape, [end]: spot };
          if (!joined || at !== neighbour) return shape;

          return end === 'to' ? { ...shape, from: spot } : { ...shape, to: spot };
        })
      );
    },
    [onShapesChange, shapes]
  );

  /**
   * Crosses a list of cells off, or puts them back — whichever `crossOff` says,
   * and it settles the question itself the first time it is asked, from the
   * state of the first cell named. Answers with what it settled on, so the rest
   * of the gesture keeps painting the same way.
   *
   * Works from `paintedRef` rather than the prop, and writes the answer back
   * into it before handing it out: see the ref's own note.
   */
  const paintCells = useCallback(
    (keys: string[], crossOff: boolean | null): boolean | null => {
      if (keys.length === 0 || !onDisabledCellsChange) return crossOff;

      const kept = paintedRef.current;
      const already = new Set(kept);
      const mode = crossOff ?? !already.has(keys[0]);
      const next = mode
        ? [...kept, ...keys.filter((key) => !already.has(key))]
        : kept.filter((cell) => !keys.includes(cell));

      // A stroke spends most of its length over holds it has already painted.
      if (next.length !== kept.length) {
        paintedRef.current = next;
        onDisabledCellsChange(next);
      }

      return mode;
    },
    [onDisabledCellsChange]
  );

  /** The cells the pointer ran over between where it was and where it now is. */
  const paintStroke = useCallback(
    (from: CalibrationPoint, to: CalibrationPoint, crossOff: boolean | null) => {
      const touched = (preview ?? [])
        .filter(
          ({ extra, point, width }) =>
            extra === null && strokeTouches(from, to, point, width / 2, (width / 2) * (aspect ?? 1))
        )
        .map(({ row, col }) => cellKey({ row, col }));

      return paintCells(touched, crossOff);
    },
    [preview, aspect, paintCells]
  );

  /**
   * Bunches a line's holds so that the one being dragged lands under the
   * pointer. One number per line, and one hold is enough to settle it — see
   * taperFor, which is also what turns away the holds that cannot say anything
   * (the two ends, and a line with nothing between them).
   */
  const dragHoldAlong = useCallback(
    (index: number, col: number, point: CalibrationPoint) => {
      if (!onShapesChange || !shapes) return;

      const shape = shapes[index];
      if (!shape) return;

      // Read against the quad rather than the picture: that is the frame the
      // holds are strung out in, so it is the one the hold's place along the
      // line has to be measured in.
      const spot = quadCoordinates(calibration, point.x, point.y);
      if (!spot) return;

      const taper = taperFor(shape, col, alongLine(shape, spot));
      if (taper === null) return;

      onShapesChange(shapes.map((current, at) => (at === index ? { ...current, taper } : current)));
    },
    [onShapesChange, shapes, calibration]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (holdDrag) {
        const point = pointFromEvent(event.clientX, event.clientY);

        return dragHoldAlong(holdDrag.index, holdDrag.col, point);
      }

      if (shapeDrag) {
        const point = pointFromEvent(event.clientX, event.clientY);
        // The same reach a hold is placed within, and refused in the same
        // places: a line cannot end on the far side of the wall's horizon.
        const spot = extraHoldSpot(calibration, point.x, point.y);

        return spot && moveShapeEnd(shapeDrag.index, shapeDrag.end, spot);
      }

      if (cellDrag) {
        const point = pointFromEvent(event.clientX, event.clientY);

        // The run since the last move, not the point it ended on: a mouse
        // crossing a wall reports every few pixels and a hold is a few pixels
        // wide, so painting only where the pointer was seen leaves the holds
        // between two sightings untouched.
        return setCellDrag(
          cellDrag.kind === 'box'
            ? { ...cellDrag, to: point }
            : { kind: 'stroke', at: point, crossOff: paintStroke(cellDrag.at, point, cellDrag.crossOff) }
        );
      }

      if (cropDrag && onCropChange) {
        const point = pointFromEvent(event.clientX, event.clientY);
        return onCropChange(
          cropDrag.handle === 'move'
            ? moveCrop(cropDrag.crop, point.x - cropDrag.from.x, point.y - cropDrag.from.y)
            : resizeCrop(cropDrag.crop, cropDrag.handle, point)
        );
      }

      if (!dragging) return;
      onChange({ ...calibration, [dragging]: pointFromEvent(event.clientX, event.clientY) });
    },
    [
      holdDrag,
      dragHoldAlong,
      shapeDrag,
      moveShapeEnd,
      cellDrag,
      paintStroke,
      cropDrag,
      onCropChange,
      dragging,
      calibration,
      onChange,
      pointFromEvent,
    ]
  );

  const startCropDrag = useCallback(
    (handle: CropHandle | 'move') => (event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setCropDrag({ handle, from: pointFromEvent(event.clientX, event.clientY), crop });
    },
    [crop, pointFromEvent]
  );

  const polygonPoints = [
    calibration.top_left,
    calibration.top_right,
    calibration.bottom_right,
    calibration.bottom_left,
  ]
    .map((p) => `${p.x * 100},${p.y * 100}`)
    .join(' ');

  /**
   * A tap on the photo puts a hold where it landed. The click is in the
   * picture's own coordinates and a hold is stored in the quad's, so it goes
   * back through the perspective — which is what keeps the hold under the
   * finger at any camera angle, and what makes it follow the corners later.
   *
   * Outside the four corners counts: a volume beside the wall, a hold above the
   * top row, anything the corners were not dragged over is still a hold to set
   * on. The quad is read past its own edges for those — see extraHoldSpot,
   * which is also what refuses the taps that are on no wall at all.
   */
  const placeExtra = useCallback(
    (event: React.MouseEvent) => {
      if (tool !== 'extras' || !onExtraHoldsChange) return;

      const point = pointFromEvent(event.clientX, event.clientY);
      const spot = extraHoldSpot(calibration, point.x, point.y);
      if (!spot) return;

      const used = Object.keys(extraHolds ?? {}).map(Number);
      const id = String(Math.max(-1, ...used) + 1);

      onExtraHoldsChange({ ...(extraHolds ?? {}), [id]: { u: spot.u, v: spot.v } });
    },
    [tool, onExtraHoldsChange, pointFromEvent, calibration, extraHolds]
  );

  /**
   * A click draws the wall, one line at a time: where it lands is the end of a
   * new line, and that line starts where the last one stopped. So a serpentine
   * is drawn the way it is walked — along a row, down to the next, back along
   * it — without ever having to say twice where a corner is.
   *
   * Held to the same reach as a hold, and refused in the same places: a point
   * beyond the wall's own horizon belongs to no part of it (see extraHoldSpot).
   */
  const drawShapePoint = useCallback(
    (event: React.MouseEvent) => {
      if (tool !== 'shapes' || !onShapesChange) return;

      const point = pointFromEvent(event.clientX, event.clientY);
      const spot = extraHoldSpot(calibration, point.x, point.y);
      if (!spot) return;

      const drawn = shapes ?? [];
      const start = drawn.length > 0 ? drawn[drawn.length - 1].to : pendingStart;
      if (!start) return setPendingStart(spot);

      setPendingStart(null);
      // The new line carries the last one's holds. A wall is drawn as runs of
      // the same length far more often than not, so the odd one out is a number
      // to correct rather than one to type on every line.
      onShapesChange([
        ...drawn,
        { from: start, to: spot, holds: drawn[drawn.length - 1]?.holds ?? 0, led_start: null },
      ]);
      onSelectShape?.(drawn.length);
    },
    [tool, onShapesChange, pointFromEvent, calibration, shapes, pendingStart, onSelectShape]
  );

  /** A click on the picture, handed to whichever tool is in hand. */
  const handlePictureClick = useCallback(
    (event: React.MouseEvent) => {
      if (tool === 'shapes') return drawShapePoint(event);

      placeExtra(event);
    },
    [tool, drawShapePoint, placeExtra]
  );

  const removeExtra = useCallback(
    (id: number) => {
      if (!onExtraHoldsChange) return;

      const next = { ...(extraHolds ?? {}) };
      delete next[String(id)];
      onExtraHoldsChange(next);
    },
    [onExtraHoldsChange, extraHolds]
  );

  /**
   * The same thing said about a whole panel at once: a volume covers thirty
   * cells, and running the pointer over each one is thirty chances to miss one.
   *
   * The box takes one answer for everything it caught, rather than each cell
   * flipping its own — a box dragged over a mixed patch has no obvious meaning
   * otherwise, and one that toggled cell by cell could not be undone by dragging
   * it again. It crosses off, unless everything caught is already crossed off,
   * in which case it puts the lot back.
   */
  const applyBox = useCallback(
    (from: CalibrationPoint, to: CalibrationPoint) => {
      const left = Math.min(from.x, to.x);
      const right = Math.max(from.x, to.x);
      const top = Math.min(from.y, to.y);
      const bottom = Math.max(from.y, to.y);

      // By where the hold is drawn, which is where the box was dragged over —
      // the cell's own row and column say nothing about that under perspective.
      const caught = (preview ?? [])
        .filter(
          ({ extra, point }) =>
            extra === null &&
            point.x >= left && point.x <= right && point.y >= top && point.y <= bottom
        )
        .map(({ row, col }) => cellKey({ row, col }));

      const already = new Set(paintedRef.current);
      paintCells(caught, caught.length > 0 && !caught.every((key) => already.has(key)));
    },
    [preview, paintCells]
  );

  /**
   * A press on the wall in cells mode. Plain, it starts painting the holds it
   * runs over; with shift it throws a box instead.
   *
   * Painting takes the drag that would otherwise pan a zoomed wall, which is the
   * right way round on this tool: crossing off a bare panel is what somebody is
   * here to do, and the wheel still moves the picture about underneath.
   */
  const startCellDrag = useCallback(
    (event: React.PointerEvent) => {
      if (tool !== 'cells' || !onDisabledCellsChange) return;

      event.preventDefault();
      // Or the frame underneath would read the same drag as a pan and carry the
      // wall along under the gesture being made on it.
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);

      const point = pointFromEvent(event.clientX, event.clientY);

      // A press that never travels is a tap on one hold, which the first step of
      // the stroke already answers — so a click needs nothing of its own.
      setCellDrag(
        event.shiftKey
          ? { kind: 'box', from: point, to: point }
          : { kind: 'stroke', at: point, crossOff: paintStroke(point, point, null) }
      );
    },
    [tool, onDisabledCellsChange, pointFromEvent, paintStroke]
  );

  const endDrag = useCallback(() => {
    if (cellDrag?.kind === 'box') applyBox(cellDrag.from, cellDrag.to);
    setCellDrag(null);
    setDragging(null);
    setCropDrag(null);
    setShapeDrag(null);
    setHoldDrag(null);
  }, [cellDrag, applyBox]);

  const cropping = tool === 'crop' && !!onCropChange;
  // Handles are grabbed, not read: left to scale with the picture, a 20px one
  // at six times zoom is a blob covering the hold it is being aimed at.
  const handleTransform = `translate(-50%, -50%) scale(${1 / zoom.scale})`;

  return (
    <div className="space-y-2">
      {/* The window the picture is looked through, and everything the zoom
          gestures belong to. It clips; the layer inside it is what moves. */}
      <div
        {...zoom.frameProps}
        data-testid="board-calibration-viewport"
        className="relative w-full select-none overflow-hidden rounded-md border bg-muted"
      >
        <div
          ref={containerRef}
          data-testid="board-calibration-frame"
          {...zoom.layerProps}
          className={`relative w-full ${
            tool === 'extras' || tool === 'cells' || tool === 'shapes' ? 'cursor-crosshair' : ''
          }`}
          onPointerDown={startCellDrag}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onClick={handlePictureClick}
        >
          <img
            src={imageUrl}
            alt=""
            className="block w-full"
            draggable={false}
            onLoad={(e) =>
              setAspect(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || null)
            }
          />

          {/* What the crop leaves out, shaded — drawn whether or not the crop is
              the tool in hand, because a board is only ever half explained by the
              picture without it: the wall everyone else sees is the bright part. */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path
              d={`M0,0 H100 V100 H0 Z M${crop.x * 100},${crop.y * 100} H${(crop.x + crop.width) * 100} V${(crop.y + crop.height) * 100} H${crop.x * 100} Z`}
              fillRule="evenodd"
              fill="rgba(0,0,0,0.55)"
            />
            <rect
              x={crop.x * 100}
              y={crop.y * 100}
              width={crop.width * 100}
              height={crop.height * 100}
              fill="none"
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="0.4"
              strokeDasharray="2 1.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {/* The lines themselves, under the holds they carry. Only while they
              are being drawn: everywhere else the wall is its holds, and a cat's
              cradle over the photo would be in the way of reading them. */}
          {tool === 'shapes' && isShapedWall(shapes) && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              {shapes.map((shape, index) => {
                const from = quadPosition(calibration, shape.from.u, shape.from.v);
                const to = quadPosition(calibration, shape.to.u, shape.to.v);
                const picked = index === selectedShape;

                return (
                  <line
                    key={`${index}-${shape.from.u}-${shape.to.v}`}
                    x1={from.x * 100}
                    y1={from.y * 100}
                    x2={to.x * 100}
                    y2={to.y * 100}
                    stroke={picked ? 'rgb(244,63,94)' : 'rgba(16,185,129,0.9)'}
                    strokeWidth={picked ? '0.9' : '0.5'}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            </svg>
          )}

          {preview?.map(({ key, row, col, point, width, extra, disabled }) => {
            const style = {
              left: `${point.x * 100}%`,
              top: `${point.y * 100}%`,
              width: `${width * 100}%`,
              aspectRatio: '1 / 1',
            };

            // The holds of the line in hand, in the line's own colour. A line is
            // a count typed into a list and a stroke on a photo, and nothing
            // else says which holes the count landed on: picking the line out of
            // the list lights them, so "12" can be checked against the wall
            // before it is saved. Only while the lines are the tool in hand —
            // elsewhere the selection is not on screen to explain the colour.
            const picked = tool === 'shapes' && extra === null && row === selectedShape;

            // A hold that is not on the grid is the gym's own, so it is the one
            // thing here that can be taken off again — in a colour of its own,
            // because the point of looking at this picture is telling the holds
            // somebody placed apart from the grid that was worked out.
            if (extra !== null) {
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={t('gymAdmin.boards.calibration.removeExtra', { id: extra + 1 })}
                  // Otherwise placing mode would count this as a tap on the photo
                  // and drop a second hold on top of the one being removed.
                  onClick={(event) => {
                    event.stopPropagation();
                    removeExtra(extra);
                  }}
                  disabled={tool !== 'extras' || !onExtraHoldsChange}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-500 bg-amber-400/40 shadow disabled:cursor-default"
                  style={style}
                />
              );
            }

            // Crossing cells off is the only thing a grid cell answers to, and
            // only while that is the tool in hand — a stray tap while aligning
            // would take a hold off the wall without anybody noticing.
            if (tool === 'cells' && onDisabledCellsChange) {
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={t(
                    disabled
                      ? 'gymAdmin.boards.calibration.enableCell'
                      : 'gymAdmin.boards.calibration.disableCell',
                    { row: row + 1, col: col + 1 }
                  )}
                  // The keyboard's way in, and only that. A pointer has already
                  // said everything it had to say by the time the click gets
                  // here — the press that started the gesture painted this cell
                  // — and acting on it again would flip it straight back. A
                  // click sent by Enter or Space carries no pointer at all, and
                  // says so in `detail`.
                  onClick={(event) => {
                    event.stopPropagation();
                    if (event.detail !== 0) return;

                    const cell = cellKey({ row, col });
                    paintCells([cell], !off.has(cell));
                  }}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${
                    disabled
                      ? 'border-dashed border-zinc-300 bg-zinc-900/40'
                      : 'border-blue-400 bg-blue-400/30'
                  }`}
                  style={style}
                />
              );
            }

            return (
              <span
                key={key}
                aria-hidden
                data-testid={picked ? 'board-picked-hold' : undefined}
                className={`pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full ${
                  disabled
                    ? 'border border-dashed border-zinc-300/80 bg-zinc-900/40'
                    : picked
                      ? 'border-2 border-rose-500 bg-rose-500/50'
                      : 'border border-blue-400/70 bg-blue-400/20'
                }`}
                style={style}
              />
            );
          })}

          {/* The calibrated area — see showQuad for when a drawn wall has one
              worth looking at. */}
          {showQuad && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polygon
                points={polygonPoints}
                fill="rgba(59,130,246,0.15)"
                stroke="rgb(59,130,246)"
                strokeWidth="0.5"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          )}

          {/* The crop window is dragged whole from inside it, so a frame that is
              the right size but in the wrong place does not have to be rebuilt
              corner by corner. Only while the crop is the tool in hand, or it
              would swallow every tap meant for the wall underneath. */}
          {cropping && (
            <div
              data-testid="board-crop-window"
              className="absolute cursor-move touch-none"
              style={{
                left: `${crop.x * 100}%`,
                top: `${crop.y * 100}%`,
                width: `${crop.width * 100}%`,
                height: `${crop.height * 100}%`,
              }}
              onPointerDown={startCropDrag('move')}
            />
          )}

          {cropping &&
            CROP_HANDLES.map((handle) => (
              <button
                key={handle}
                type="button"
                aria-label={t(`gymAdmin.boards.calibration.cropHandles.${handle}`)}
                onPointerDown={startCropDrag(handle)}
                className="absolute h-5 w-5 rounded-sm border-2 border-blue-500 bg-white shadow touch-none"
                style={{
                  left: `${(handle.includes('w') ? crop.x : crop.x + crop.width) * 100}%`,
                  top: `${(handle.includes('n') ? crop.y : crop.y + crop.height) * 100}%`,
                  transform: handleTransform,
                }}
              />
            ))}

          {/* The stretch of wall being crossed off in one go. Drawn on the picture
              rather than over the frame so it stays on the holds it caught while
              the wall is panned about under it. */}
          {cellDrag?.kind === 'box' && (
            <div
              data-testid="board-cell-marquee"
              aria-hidden
              className="pointer-events-none absolute border-2 border-dashed border-blue-400 bg-blue-400/20"
              style={{
                left: `${Math.min(cellDrag.from.x, cellDrag.to.x) * 100}%`,
                top: `${Math.min(cellDrag.from.y, cellDrag.to.y) * 100}%`,
                width: `${Math.abs(cellDrag.to.x - cellDrag.from.x) * 100}%`,
                height: `${Math.abs(cellDrag.to.y - cellDrag.from.y) * 100}%`,
              }}
            />
          )}

          {/* Where the first line will start, until the click that gives it an
              end. Nothing to take hold of yet — there is no line to move. */}
          {tool === 'shapes' && pendingStart && (
            <span
              aria-hidden
              data-testid="board-shape-start"
              className="pointer-events-none absolute size-3 rounded-full border-2 border-white bg-emerald-500 shadow"
              style={{
                left: `${quadPosition(calibration, pendingStart.u, pendingStart.v).x * 100}%`,
                top: `${quadPosition(calibration, pendingStart.u, pendingStart.v).y * 100}%`,
                transform: handleTransform,
              }}
            />
          )}

          {tool === 'shapes' &&
            shapeHandles.map(({ index, end, point }) => {
              const at = quadPosition(calibration, point.u, point.v);

              return (
                <button
                  key={`${index}-${end}`}
                  type="button"
                  aria-label={t('gymAdmin.boards.calibration.shapeEnd', { line: index + 1 })}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    // The frame reads a drag on the wall as a pan, and the
                    // picture reads the click that ends it as a new line.
                    event.stopPropagation();
                    setShapeDrag({ index, end });
                    onSelectShape?.(index);
                  }}
                  onClick={(event) => event.stopPropagation()}
                  className={`absolute size-4 rounded-sm border-2 border-white shadow touch-none ${
                    index === selectedShape ? 'bg-rose-500' : 'bg-emerald-500'
                  }`}
                  style={{
                    left: `${at.x * 100}%`,
                    top: `${at.y * 100}%`,
                    transform: handleTransform,
                  }}
                />
              );
            })}

          {/* The holds of the line in hand, as things to take hold of: drag one
              onto the hole it belongs to and the whole run spaces itself to
              match. Sized like the hold it sits on and all but transparent, so
              what the eye follows is still the hold underneath — these are a
              grip on it, not another circle to read. */}
          {taperHandles.map(({ key, row, col, point, width }) => (
            <button
              key={`taper-${key}`}
              type="button"
              data-testid="board-hold-handle"
              aria-label={t('gymAdmin.boards.calibration.holdAlong', {
                hold: col + 1,
                line: row + 1,
              })}
              onPointerDown={(event) => {
                event.preventDefault();
                // The frame reads a drag on the wall as a pan, and the picture
                // reads the click that ends it as another line.
                event.stopPropagation();
                setHoldDrag({ index: row, col });
              }}
              onClick={(event) => event.stopPropagation()}
              // Back to evenly spaced, which is the way out of a drag that ran
              // off to one end and left every hold piled up there — too small a
              // target to drag back.
              onDoubleClick={(event) => {
                event.stopPropagation();
                onShapesChange?.(
                  (shapes ?? []).map((shape, at) => (at === row ? { ...shape, taper: null } : shape))
                );
              }}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full ring-1 ring-white/70 touch-none active:cursor-grabbing"
              style={{
                left: `${point.x * 100}%`,
                top: `${point.y * 100}%`,
                width: `${width * 100}%`,
                aspectRatio: '1 / 1',
              }}
            />
          ))}

          {showQuad &&
            CORNERS.map((corner) => {
              const point = calibration[corner];
              return (
                <button
                  key={corner}
                  type="button"
                  aria-label={t(`gymAdmin.boards.calibration.corners.${corner}`)}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    // The frame reads a drag on the wall as a pan; a drag that
                    // starts on a handle is the handle's.
                    e.stopPropagation();
                    setDragging(corner);
                  }}
                  // Out of the way while the crop is being framed: the two sets of
                  // handles sit on top of each other on a wall that fills its photo.
                  className={`absolute h-5 w-5 rounded-full border-2 border-white bg-blue-500 shadow ring-1 ring-blue-500 touch-none ${
                    cropping || tool === 'shapes' ? 'pointer-events-none opacity-50' : ''
                  }`}
                  style={{
                    left: `${point.x * 100}%`,
                    top: `${point.y * 100}%`,
                    transform: handleTransform,
                  }}
                />
              );
            })}
        </div>

        {/* Down the left edge, clear of the wall itself — the same stack a board
            photo carries everywhere else it can be looked at closely. Outside the
            layer, so the buttons do not travel with the picture they move.
            The frame around them reads a press as the start of a pan and takes
            the pointer once it travels; a press meant for a button is kept from
            it, or a jiggle on the way to a click would swallow the click. */}
        <div
          className="absolute left-2 top-1/2 flex -translate-y-1/2 flex-col gap-1"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={zoom.zoomIn}
            disabled={!zoom.canZoomIn}
            title={t('climbs.board.zoom.in')}
            aria-label={t('climbs.board.zoom.in')}
            className={CONTROL}
          >
            <ZoomIn className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={zoom.zoomOut}
            disabled={!zoom.zoomed}
            title={t('climbs.board.zoom.out')}
            aria-label={t('climbs.board.zoom.out')}
            className={CONTROL}
          >
            <ZoomOut className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={zoom.reset}
            disabled={!zoom.zoomed}
            title={t('climbs.board.zoom.reset')}
            aria-label={t('climbs.board.zoom.reset')}
            className={CONTROL}
          >
            <Scan className="size-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The window with one corner dragged to a point, the opposite one staying put.
 * Never smaller than MIN_CROP_SIZE: a handle pushed past its opposite would
 * otherwise turn the window inside out.
 */
function resizeCrop(crop: BoardImageCrop, handle: CropHandle, point: CalibrationPoint): BoardImageCrop {
  const right = crop.x + crop.width;
  const bottom = crop.y + crop.height;

  const x = handle.includes('w') ? Math.min(point.x, right - MIN_CROP_SIZE) : crop.x;
  const y = handle.includes('n') ? Math.min(point.y, bottom - MIN_CROP_SIZE) : crop.y;

  return normalizeCrop({
    x,
    y,
    width: handle.includes('w') ? right - x : Math.max(MIN_CROP_SIZE, point.x - x),
    height: handle.includes('n') ? bottom - y : Math.max(MIN_CROP_SIZE, point.y - y),
  });
}

/**
 * Whether a hold drawn at `centre` is touched by the pointer's run from `from`
 * to `to` — the segment, not its two ends.
 *
 * A hold is a circle on screen but an ellipse in these coordinates, which are
 * fractions of the picture's width one way and of its height the other. So both
 * are measured in units of the hold's own radii, where "touched" is a distance
 * of one, and the question becomes how near the segment passes to a point.
 */
function strokeTouches(
  from: CalibrationPoint,
  to: CalibrationPoint,
  centre: CalibrationPoint,
  radiusX: number,
  radiusY: number
): boolean {
  if (radiusX <= 0 || radiusY <= 0) return false;

  const ax = (from.x - centre.x) / radiusX;
  const ay = (from.y - centre.y) / radiusY;
  const dx = (to.x - centre.x) / radiusX - ax;
  const dy = (to.y - centre.y) / radiusY - ay;

  const length = dx * dx + dy * dy;
  // How far along the run the nearest point lies, held to the run's own ends so
  // a hold behind where the pointer came from does not count as passed over.
  const along = length === 0 ? 0 : Math.min(1, Math.max(0, -(ax * dx + ay * dy) / length));

  return Math.hypot(ax + along * dx, ay + along * dy) <= 1;
}

/** The same window shifted, held inside the picture rather than resized by it. */
function moveCrop(crop: BoardImageCrop, dx: number, dy: number): BoardImageCrop {
  return {
    ...crop,
    x: Math.min(1 - crop.width, Math.max(0, crop.x + dx)),
    y: Math.min(1 - crop.height, Math.max(0, crop.y + dy)),
  };
}
